/**
 * NuGuard AI - Main Layout Component
 *
 * Provides the main application layout with responsive sidebar navigation,
 * mobile hamburger menu, skip link for accessibility, and React Router outlet.
 */

import React, { useState, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { Menu } from 'lucide-react';
import Sidebar from './Sidebar';
import InvitationBanner from './InvitationBanner';
import TrialBanner from './TrialBanner';
const FloatingAssistant = React.lazy(() => import('./FloatingAssistant'));
import { User } from '../types';

interface LayoutProps {
  user: User;
  onLogout: () => void;
}

const Layout: React.FC<LayoutProps> = ({ user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('nuguard-sidebar-collapsed') === 'true';
  });
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('nuguard-sidebar-width');
    return saved ? Math.max(200, Math.min(400, Number(saved))) : 256;
  });

  const COLLAPSED_WIDTH = 64;
  const effectiveWidth = sidebarCollapsed ? COLLAPSED_WIDTH : sidebarWidth;

  const handleCloseSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const handleSidebarResize = useCallback((width: number) => {
    // Auto-collapse when dragged below threshold, auto-expand when dragged above
    const COLLAPSE_THRESHOLD = 120;
    if (width < COLLAPSE_THRESHOLD) {
      if (!sidebarCollapsed) {
        setSidebarCollapsed(true);
        localStorage.setItem('nuguard-sidebar-collapsed', 'true');
      }
    } else {
      if (sidebarCollapsed) {
        setSidebarCollapsed(false);
        localStorage.setItem('nuguard-sidebar-collapsed', 'false');
      }
      setSidebarWidth(width);
      localStorage.setItem('nuguard-sidebar-width', String(width));
    }
  }, [sidebarCollapsed]);

  return (
    <div className="min-h-screen bg-nuguard-light-bg dark:bg-nuguard-bg transition-colors duration-200">
      {/* Skip link for keyboard/screen-reader users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:text-sm"
      >
        Skip to main content
      </a>

      {/* Sidebar Navigation */}
      <Sidebar
        user={user}
        onLogout={onLogout}
        isOpen={sidebarOpen}
        onClose={handleCloseSidebar}
        width={sidebarWidth}
        onResize={handleSidebarResize}
        collapsed={sidebarCollapsed}
      />

      {/* Mobile Header Bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-10 bg-nuguard-light-card dark:bg-nuguard-bg border-b border-nuguard-light-edge dark:border-nuguard-edge/60 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg text-stone-600 dark:text-slate-400 hover:bg-stone-100 dark:hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          aria-label="Open navigation menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <span className="text-lg font-semibold text-stone-900 dark:text-white">NuGuard AI</span>
      </header>

      {/* Main Content Area */}
      <main
        id="main-content"
        className="min-h-screen pt-14 md:pt-0"
        style={{ marginLeft: undefined }}
      >
        {/* Use inline style on desktop for dynamic sidebar width */}
        <style>{`@media (min-width: 768px) { #main-content { margin-left: ${effectiveWidth}px !important; transition: margin-left 200ms ease; } }`}</style>
        <TrialBanner user={user} />
        <InvitationBanner onAccepted={() => window.location.reload()} />
        <div className="p-3 md:p-5 lg:p-6 2xl:p-8">
          <Outlet />
        </div>
      </main>
      <React.Suspense fallback={null}><FloatingAssistant /></React.Suspense>
    </div>
  );
};

export default Layout;
